import React, { useState, useMemo } from 'react';

// MUI
import { Close, ExpandMore } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Accordion,
  TextField,
  Typography,
  IconButton,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';

// Third party
import { Controller, useFormContext } from 'react-hook-form';

type TRHFMultiOptionSelectProps<T> = {
  name: string;
  label: string;
  options: T[];
  getId: (item: T) => string;
  getLabel: (item: T) => string;
  isEdit?: boolean;
  summaryName?: string;
};

export function RHFMultiOptionSelect<T>({
                                          name,
                                          label,
                                          summaryName,
                                          options,
                                          getId,
                                          getLabel,
                                          isEdit,
                                        }: TRHFMultiOptionSelectProps<T>) {
  const { control } = useFormContext();
  const [search, setSearch] = useState('');

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const selected: string[] = field.value ?? [];

        const filtered = useMemo(
          () =>
            options.filter((o) =>
              getLabel(o).toLowerCase().includes(search.toLowerCase())
            ),
          [options, search, getLabel]
        );

        const handleSelectAll = () => {
          const allIds = options.map((o) => getId(o));
          field.onChange(allIds);
        };

        const handleUnselectAll = () => {
          field.onChange([]);
        };

        const toggle = (id: string) => {
          if (selected.includes(id)) {
            field.onChange(selected.filter((x) => x !== id));
          } else {
            field.onChange([...selected, id]);
          }
        };

        return (
          <Accordion
            disableGutters
            sx={{
              p: .4,
              background: 'transparent',
              border: error
                ? '.5px solid #d32f2f'
                : '1px solid #868B8F',
              borderRadius: '5px',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore sx={{ color: `${error ? '#D9363E': '#868B8F'}` }} />}>
              <Typography
                sx={{ color: `${error ? '#D9363E': '#868B8F'}` }}
              >
                {summaryName}
              </Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Box>
                {!isEdit && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      pb: 0,
                    }}
                  >
                    <TextField
                      placeholder="Search..."
                      size="small"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          height: 36,
                        },
                        '& .MuiInputBase-input': {
                          color: 'white',
                          fontSize: '14px',
                        },
                      }}
                    />

                    {search.length > 0 ? (
                      <IconButton
                        onClick={() => setSearch('')}
                        sx={{
                          padding: '4px',
                          color: '#A8B2D1',
                          '&:hover': { color: '#1DB0BF' },
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    ): (
                      <>
                        {selected.length === options.length && options.length > 0 ? (
                          <Button
                            onClick={handleUnselectAll}
                            sx={{ color: '#A8B2D1', fontSize: '13px', minWidth: 0 }}
                          >
                            Unselect All
                          </Button>
                        ): (
                          <Button
                            onClick={handleSelectAll}
                            sx={{ color: '#1DB0BF', fontSize: '13px', minWidth: 0 }}
                          >
                            Select All
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                )}

                {/* List */}
                <Box
                  sx={{
                    maxHeight: 230,
                    overflowY: 'auto',
                    borderRadius: '8px',
                    backgroundColor: '#0E1720',
                    mt: 2,
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#FFF',
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#151E27',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 1.5,
                    }}
                  >
                    {filtered.map((item) => {
                      const id = getId(item);
                      const labelText = getLabel(item);
                      const isSelected = selected.includes(id);

                      return (
                        <Box
                          key={id}
                          onClick={() => !isEdit && toggle(id)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 1.5,
                            borderRadius: '8px',
                            backgroundColor: isSelected
                              ? 'rgba(29, 176, 191, 0.18)'
                              : '#151E27',
                            border: '1px solid #394047',
                            cursor: isEdit ? 'default': 'pointer',
                            transition: '0.15s',
                            ...(isEdit
                              ? {}
                              : {
                                '&:hover': {
                                  backgroundColor: '#1E2630',
                                  borderColor: '#4A535A',
                                },
                              }),
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '14px',
                              color: 'white',
                              pr: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {labelText}
                          </Typography>

                          <Checkbox
                            checked={isSelected}
                            disabled={isEdit}
                            sx={{
                              color: '#A8B2D1',
                              '&.Mui-checked': {
                                color: '#1DB0BF',
                              },
                              '&.Mui-disabled': {
                                color: '#555',
                              },
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      }}
    />
  );
}
